import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, ArrowUpRight, Heart, Flame,
  Trophy, Quotes, ChartLineUp, Newspaper,
} from "@phosphor-icons/react";
import Nav from "@/components/site/Nav";
import FlagStripe from "@/components/site/FlagStripe";
import PledgeWidget from "@/components/site/PledgeWidget";
import PledgeTracker from "@/components/site/PledgeTracker";
import MovingForShare from "@/components/site/MovingForShare";
import SacrificeTribute from "@/pages/sacrifice/SacrificeTribute";
import SacrificeHero from "@/pages/sacrifice/SacrificeHero";
import SacrificeTwoWaysToJoin from "@/pages/sacrifice/SacrificeTwoWaysToJoin";
import SacrificeFinalCTA from "@/pages/sacrifice/SacrificeFinalCTA";
import SacrificeFeaturedIn from "@/pages/sacrifice/SacrificeFeaturedIn";
import KmsFeaturedStrip from "@/pages/sacrifice/KmsFeaturedStrip";
import SignupStepper from "@/pages/sacrifice/SignupStepper";
import EventCard from "@/components/site/EventCard";
import { UPCOMING_EVENTS } from "@/data/events";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

import { identify as swsIdentify, trackConversion, getAttributionSnapshot } from "@/lib/analyticsClient";
import { shopHref } from "@/lib/kmsAccess";

const TIERS = [
  { id: "merch", amount: 0, label: "Buy Legacy Merch", helper: "Submit → go to shop · net proceeds split 50/50 between T2T + FDNY Fire Family Transport" },
  { id: "custom", amount: 20, label: "Donate direct", helper: "100% to Pledge · split 50/50 between T2T + FDNY Fire Family Transport" },
];

// STEPS / PACES / PRIZES moved to /pages/sacrifice/SacrificeTwoWaysToJoin.jsx

function PaymentBanner({ status, onDismiss }) {
  if (!status) return null;
  const isSuccess = status.payment_status === "paid";
  const isCanceled = status.canceled;
  const isProcessing = status.payment_status === "pending" || status.payment_status === "processing";
  const isExpired = status.status === "expired" || status.payment_status === "failed";
  const isMerchPending = status.merch_pending || status.status === "merch_pending";

  return (
    <div
      data-testid="payment-banner"
      className={`px-6 md:px-12 py-5 border-b-2 ${
        isSuccess ? "bg-emerald-600 border-emerald-300 text-white" :
        isMerchPending ? "bg-emerald-700 border-emerald-300 text-white" :
        isCanceled ? "bg-yellow-600 border-yellow-300 text-white" :
        isExpired ? "bg-red-700 border-red-400 text-white" :
        "bg-[#B32027] border-white/40 text-white"
      }`}
    >
      <div className="max-w-5xl mx-auto flex items-start gap-4 justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] font-bold mb-1">
            {isSuccess ? "Payment confirmed" :
             isMerchPending ? "Merch entry received" :
             isCanceled ? "Payment canceled" :
             isExpired ? "Payment session expired" :
             isProcessing ? "Checking your payment" : "Payment update"}
          </p>
          <p className="text-base md:text-lg leading-snug">
            {isSuccess && "You're officially in. Watch your email for next steps and your participant log link."}
            {isMerchPending && "Your entry is saved. Head to the shop, pick your piece, and we'll lock your name on the Legacy Wall right after checkout. Thank you."}
            {isCanceled && "No charge was made. Resubmit the form below whenever you're ready."}
            {isExpired && "Your checkout session expired. Please try again."}
            {isProcessing && "Hang tight — we're confirming with Stripe…"}
          </p>
        </div>
        <button onClick={onDismiss} className="text-white/80 hover:text-white text-xs uppercase tracking-[0.25em] font-bold" data-testid="dismiss-payment-banner">
          Dismiss
        </button>
      </div>
    </div>
  );
}

function FeaturedDrop({ merchPreview, featuredOverrideId, onSelectMerch }) {
  const products = merchPreview?.products || [];
  if (products.length === 0) return null;
  // Prefer admin-pinned product if set + still in stock; otherwise pick the highest-priced
  const override = featuredOverrideId
    ? products.find((p) => Number(p.id) === Number(featuredOverrideId))
    : null;
  const featured = override
    || [...products].sort((a, b) => (b.price_to || b.price_from || 0) - (a.price_to || a.price_from || 0))[0];
  const priceLabel = featured.price_to && featured.price_to !== featured.price_from
    ? `$${Math.round(featured.price_from)}–$${Math.round(featured.price_to)}`
    : `$${Math.round(featured.price_from || 0)}`;

  return (
    <section
      data-testid="featured-drop"
      className="px-4 md:px-8 py-16 md:py-24 max-w-6xl mx-auto border-t border-white/10"
    >
      <p className="text-[#B32027] uppercase tracking-[0.4em] text-xs font-bold mb-4">/ Featured drop</p>
      <h2 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-4xl md:text-6xl mb-10">
        Sport the <span className="text-[#B32027]">tribute.</span>
      </h2>
      <div className="grid md:grid-cols-2 gap-6 md:gap-10 border-2 border-[#B32027] bg-[#0F0F0F] p-6 md:p-10">
        <div className="aspect-square bg-[#141414] overflow-hidden">
          {featured.thumbnail_url && (
            <img
              src={featured.thumbnail_url}
              alt={featured.name}
              className="w-full h-full object-cover"
              loading="lazy"
              data-testid="featured-drop-image"
            />
          )}
        </div>
        <div className="flex flex-col justify-center gap-5">
          <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] font-bold text-[#B32027]">
            <Flame size={12} weight="fill" /> Top piece · live inventory
          </span>
          <h3 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-3xl md:text-5xl">
            {featured.name}
          </h3>
          <p data-testid="featured-drop-price" className="text-3xl font-bold text-white">
            {priceLabel}
          </p>
          <p className="text-white/70 text-base md:text-lg leading-relaxed">
            The signature piece of the inaugural drop. Premium construction — priced so as much profit as possible carries a name forward.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 pt-3">
            <Link
              to={`/merch/checkout?product=${featured.id}`}
              data-testid="featured-drop-buy"
              className="inline-flex items-center justify-center gap-2 bg-[#B32027] hover:bg-[#D42730] text-white px-6 py-4 text-[11px] font-bold uppercase tracking-[0.3em]"
            >
              Buy now · Stripe <ArrowUpRight size={14} weight="bold" />
            </Link>
            <Link
              to={shopHref()}
              data-testid="featured-drop-see-all"
              className="inline-flex items-center justify-center gap-2 border-2 border-white/25 hover:border-[#B32027] hover:text-[#B32027] text-white px-6 py-4 text-[11px] font-bold uppercase tracking-[0.3em] transition-colors"
            >
              See all merch
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Sacrifice({ kmsMode = false }) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [tier, setTier] = useState("custom");
  const [pledgeCtaFlash, setPledgeCtaFlash] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [merchPreview, setMerchPreview] = useState(null); // {products:[], store_url:''}
  const [shopSettings, setShopSettings] = useState({ featured_product_id: null });
  const [legacyPhotos, setLegacyPhotos] = useState([]);   // File[]
  const [primaryPhotoIdx, setPrimaryPhotoIdx] = useState(0);
  const [prefill, setPrefill] = useState({ name: "", email: "" });
  const [postPurchaseSrc, setPostPurchaseSrc] = useState("");
  const [submitToast, setSubmitToast] = useState(null); // { tier: 'merch'|'custom', name?: str }
  const [signupInView, setSignupInView] = useState(false);
  const pollRef = useRef(null);
  const signupSectionRef = useRef(null);

  // Auto-dismiss the post-submit toast after 45s so it doesn't linger forever
  // if the user opens the Pledge widget in a new tab.
  useEffect(() => {
    if (!submitToast || submitToast.tier === "merch") return;
    const t = setTimeout(() => setSubmitToast(null), 45000);
    return () => clearTimeout(t);
  }, [submitToast]);

  // Hide the sticky "Join the Legacy" CTA while the signup form is on-screen
  // (per launch spec — the sticky is a nudge, not a duplicate).
  useEffect(() => {
    const el = signupSectionRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      ([entry]) => setSignupInView(entry.isIntersecting),
      { rootMargin: "-15% 0px -15% 0px", threshold: 0 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Load live merch preview once — used inside the merch tier + inline strip
  useEffect(() => {
    let alive = true;
    fetch(`${BACKEND}/api/shop/products`)
      .then((r) => r.json())
      .then((d) => alive && setMerchPreview(d))
      .catch(() => alive && setMerchPreview({ products: [], store_url: "" }));
    fetch(`${BACKEND}/api/shop/settings`)
      .then((r) => r.json())
      .then((d) => alive && setShopSettings(d || { featured_product_id: null }))
      .catch(() => { /* ignore */ });
    return () => { alive = false; };
  }, []);

  // On mount: detect session_id / canceled in the URL and poll status
  useEffect(() => {
    const url = new URL(window.location.href);
    // Post-purchase / post-donation return: pre-fill name+email so users don't have to retype.
    const emailParam = url.searchParams.get("email");
    const nameParam = url.searchParams.get("name");
    const srcParam = url.searchParams.get("src"); // "merch" | "donate"
    if (emailParam || nameParam) {
      setPrefill({ name: nameParam || "", email: emailParam || "" });
    }
    if (srcParam) {
      setPostPurchaseSrc(srcParam);
      // Auto-scroll to the signup form so the "one last step" flow feels seamless.
      setTimeout(() => document.getElementById("legacy-signup")?.scrollIntoView({ behavior: "smooth", block: "start" }), 400);
    }
    // Clean the URL after reading — keep #signup so refresh still lands on the form.
    if (emailParam || nameParam || srcParam) {
      ["email", "name", "src"].forEach((k) => url.searchParams.delete(k));
      window.history.replaceState({}, "", url.pathname + url.search + (srcParam ? "#signup" : url.hash));
    }
    // Honor ?tier=merch (or 20/custom) coming from nav / sticky bar — pre-select and scroll to form
    const tierParam = url.searchParams.get("tier");
    if (tierParam === "merch" || tierParam === "20" || tierParam === "custom") {
      setTier(tierParam);
      url.searchParams.delete("tier");
      window.history.replaceState({}, "", url.pathname + url.search + "#signup");
      setTimeout(() => document.getElementById("legacy-signup")?.scrollIntoView({ behavior: "smooth" }), 300);
    }
    const sessionId = url.searchParams.get("session_id");
    const canceled = url.searchParams.get("canceled");
    if (canceled) {
      setPaymentStatus({ canceled: true });
      url.searchParams.delete("canceled");
      window.history.replaceState({}, "", url.pathname + url.search + url.hash);
      return;
    }
    if (!sessionId) return;

    let attempts = 0;
    const maxAttempts = 10;
    setPaymentStatus({ payment_status: "pending" });

    const poll = async () => {
      attempts += 1;
      try {
        const r = await fetch(`${BACKEND}/api/sacrifice/checkout/status/${sessionId}`);
        if (!r.ok) throw new Error("status fetch failed");
        const data = await r.json();
        if (data.payment_status === "paid") {
          setPaymentStatus({ payment_status: "paid", amount_total: data.amount_total });
          url.searchParams.delete("session_id");
          window.history.replaceState({}, "", url.pathname + url.search + url.hash);
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") {
          setPaymentStatus({ status: "expired", payment_status: data.payment_status });
          return;
        }
        if (attempts < maxAttempts) {
          pollRef.current = setTimeout(poll, 2000);
        } else {
          setPaymentStatus({ payment_status: "pending", timeout: true });
        }
      } catch {
        if (attempts < maxAttempts) pollRef.current = setTimeout(poll, 2500);
      }
    };
    poll();
    return () => { if (pollRef.current) clearTimeout(pollRef.current); };
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true); setError("");
    const f = new FormData(e.currentTarget);
    const payload = {
      name: f.get("name"),
      email: f.get("email"),
      phone: f.get("phone") || "",
      location: f.get("location") || "",
      tier,  // "merch" or "custom" (custom = direct donation via Pledge)
      custom_amount: null,  // Pledge handles the actual donation amount inline
      moving_for_name: f.get("moving_for_name") || "",
      moving_for_relationship: f.get("moving_for_relationship") || "",
      moving_for_category: f.get("moving_for_freetext") || "",
      story: f.get("story") || "",
      sharing_consent: f.get("sharing_consent") || "full",
      leaderboard_optin: f.get("leaderboard_optin") || "no",
      movement_plan: f.get("movement_plan") || "",
      referred_by: f.get("referred_by") || "",
      hear_about: f.get("hear_about") || "",
    };
    try {
      // Step 1 — lock in the signup (Wall entry). Attach the analytics
      // attribution snapshot so this signup is credited to the traffic source
      // (IG / LinkedIn / email / KMS / QR / etc.) that brought them.
      const r = await fetch(`${BACKEND}/api/sacrifice/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...payload, attribution: getAttributionSnapshot() }),
      });
      if (!r.ok) {
        const txt = await r.text();
        throw new Error(txt || "Submission failed");
      }
      const signup = await r.json();

      // Sweat With Slay analytics — link session to identity + record conversion
      try {
        swsIdentify(payload.email, tier === "merch" ? "sacrifice_signup_merch" : "sacrifice_signup_donate", payload.name || "");
        trackConversion(tier === "merch" ? "sacrifice_signup_merch" : "sacrifice_signup_donate", {
          email: payload.email,
          refId: signup.id || "",
        });
      } catch { /* silent */ }

      // Optional multi-photo upload (Legacy Wall)
      if (legacyPhotos.length > 0) {
        for (let i = 0; i < legacyPhotos.length; i++) {
          const photoFile = legacyPhotos[i];
          try {
            const fd = new FormData();
            fd.append("email", payload.email);
            fd.append("file", photoFile);
            fd.append("caption", payload.story || "");
            fd.append("primary", i === primaryPhotoIdx ? "true" : "false");
            await fetch(`${BACKEND}/api/sacrifice/photo`, { method: "POST", body: fd });
          } catch (photoErr) {
            console.warn(`Legacy photo ${i + 1} upload failed (continuing):`, photoErr);
          }
        }
      }

      // Merch tier — save signup then present the inline success card.
      // NO auto-redirect: the user picks "Continue to merch checkout" from
      // the 4 next-step buttons themselves. Their signup_id is threaded via
      // localStorage + query param so /shop can auto-confirm the Wall entry.
      if (tier === "merch") {
        try {
          window.localStorage.setItem("sws_pending_signup", signup.id || "");
          window.localStorage.setItem("sws_pending_signup_email", payload.email || "");
        } catch (err) { console.warn("Sacrifice: pending-signup storage write failed:", err); }
        setSubmitting(false);
        setError("");
        setSubmitToast({ tier: "merch", name: payload.name, moving_for: payload.moving_for_name || "", signup_id: signup.id || "" });
        // Scroll to the success card so the user actually sees it — form is above the fold.
        setTimeout(() => document.getElementById("signup-success-anchor")?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
        return;
      }

      // Donation tier — no Stripe. Their Wall entry is locked in; show the inline success card
      // with the 4 next-step buttons. Pledge widget still lives below for whoever taps "Donate".
      setSubmitting(false);
      setError("");
      setPledgeCtaFlash(true);
      setSubmitToast({ tier: "custom", name: payload.name, moving_for: payload.moving_for_name || "", signup_id: signup.id || "" });
      setTimeout(() => setPledgeCtaFlash(false), 4000);
      setTimeout(() => document.getElementById("signup-success-anchor")?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again or email sweatwithslay@gmail.com.");
      setSubmitting(false);
    }
  };

  // Legacy Wall submissions are paid-only — they flow through the signup form's photo input,
  // uploaded server-side after the /api/sacrifice/signup call (see onSubmit above).

  return (
    <main data-testid="sacrifice-page" className="bg-[#0A0A0A] text-white font-body min-h-screen pb-24 sm:pb-20 pt-20">
      <Nav />
      <FlagStripe position="top" data-testid="sacrifice-flag-stripe-top" />
      {/* Top bar */}
      <div className="border-b border-white/10 px-6 md:px-12 py-4 flex items-center justify-between">
        <Link to="/" className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] font-bold text-white/70 hover:text-[#B32027]">
          <ArrowLeft size={12} weight="bold" /> Sweat With Slay
        </Link>
        <div className="flex items-center gap-4">
          <Link to="/sweat-for-sacrifice/leaderboard" data-testid="nav-leaderboard" className="hidden sm:inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] font-bold text-white/70 hover:text-[#B32027]">
            <Trophy size={12} weight="fill" /> Leaderboard
          </Link>
          <Link to="/legacy-wall" data-testid="nav-legacy-wall" className="hidden sm:inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] font-bold text-white/70 hover:text-[#B32027]">
            <Heart size={12} weight="fill" /> Legacy Wall
          </Link>
          <Link to="/sweat-for-sacrifice/log" data-testid="nav-log" className="hidden sm:inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] font-bold text-white/70 hover:text-[#B32027]">
            <ChartLineUp size={12} weight="fill" /> Log Minutes
          </Link>
          <Link to="/news" data-testid="nav-news" className="hidden lg:inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] font-bold text-white/70 hover:text-[#B32027]">
            <Newspaper size={12} weight="fill" /> In the News
          </Link>
          <a
            href="#legacy-signup"
            onClick={(e) => { e.preventDefault(); document.getElementById("legacy-signup")?.scrollIntoView({ behavior: "smooth", block: "start" }); }}
            className="inline-flex items-center gap-2 bg-[#B32027] hover:bg-[#D42730] text-white px-5 py-2.5 text-[10px] font-bold uppercase tracking-[0.25em]"
          >
            Join the Legacy
          </a>
        </div>
      </div>

      <PaymentBanner status={paymentStatus} onDismiss={() => setPaymentStatus(null)} />

      {/* KMS partner banner — placed AFTER the top bar so nothing overlaps
          it. Only renders when visitor entered via /kms; same challenge,
          same signup, same wall — this is a courtesy note + shortcut. */}
      {kmsMode && (
        <div
          data-testid="kms-landing-banner"
          className="bg-gradient-to-r from-[#003366] via-[#0A0A0A] to-[#B32027] text-white px-5 md:px-12 py-6 md:py-5 border-b border-white/20"
        >
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="min-w-0">
              <div className="text-[11px] md:text-[12px] uppercase tracking-[0.3em] font-bold text-white mb-2">
                KMS Technology × Sweat With Slay
              </div>
              <div className="text-white/80 text-[13px] md:text-sm leading-relaxed">
                Same challenge · same Legacy Wall · two team-exclusive pieces in the shop.
              </div>
            </div>
            <Link
              to="/kms/shop"
              data-testid="kms-banner-shop-link"
              className="self-start md:self-auto inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] font-bold border border-white/50 hover:border-white bg-white/5 hover:bg-white/10 text-white px-5 py-3 whitespace-nowrap"
            >
              KMS shop →
            </Link>
          </div>
        </div>
      )}

      {/* Hero */}
      <SacrificeHero />

      {/* Campaign progress tracker moved to bottom of page (below Final CTA)
          per user request — final touchpoint after the emotional close. */}

      {/* In Honor Of + Where It Started — extracted to /pages/sacrifice/SacrificeTribute.jsx */}
      <SacrificeTribute />

      {/* How It Works — merged into "Two ways to join" grid below (see Donate section) */}

      {/* Featured Drop — moved BELOW all donate/challenge/foundation content per user request */}

      {/* Two ways to join · The Challenge · Where money goes — ONE big 2-col layout per user request */}
      <SacrificeTwoWaysToJoin tier={tier} setTier={setTier} merchPreview={merchPreview} />

      {/* Pledge donate widget lives INLINE inside the signup form when the "Donate direct" tier is picked. */}

      {/* Featured Drop moved BELOW the signup form per user request — form must come first */}

      {/* Legacy merch carousel removed per user request — not needed before signup */}

      {/* Where It Started — MOVED EARLIER (right after In Honor Of) */}

      {/* About Them — merged into "In Honor Of" bio cards above to remove duplication */}

      {/* Who Are You Moving For — single bridge to the Legacy Wall (REMOVED — merged into Legacy Wall below) */}

      {/* Legacy Wall preamble removed per user request — sign-up form starts immediately */}

      {/* The Challenge — merged into the "Two ways to join" flow above (per user restructure) */}

      {/* Fundraising & Donation Transparency — merged into the "Two ways to join" flow above (per user restructure) */}

      {/* Featured in the news — credibility moment placed AFTER the tribute
          story and directly ABOVE the signup form, so visitors understand
          the "why" first, then see the coverage right before they join. */}
      <SacrificeFeaturedIn />

      {/* Sign-up form — MOST IMPORTANT SECTION (highlighted, elevated) */}
      {/* Sign-up + Support + Confirmation stepper (Feb 2026 launch flow) */}
      <div ref={signupSectionRef}>
        <SignupStepper kmsMode={kmsMode} />
      </div>

      {/* KMS Exclusive featured strip — only renders on /kms landing.
          Sits DIRECTLY under the signup form. Clicking either the Tee/Polo
          card or the "Shop the full drop" CTA routes to /kms/shop where
          the same two pieces surface again + the full public catalog. */}
      {kmsMode && <KmsFeaturedStrip />}

      {/* Featured Drop — placed AFTER the signup form per user request so the form
          comes first and Sport-the-Tribute lives as a reward/nudge below. */}
      <FeaturedDrop
        merchPreview={merchPreview}
        featuredOverrideId={shopSettings.featured_product_id}
        onSelectMerch={() => { setTier("merch"); document.getElementById("legacy-signup")?.scrollIntoView({ behavior: "smooth" }); }}
      />

      {/* The Movement Continues — Tunnel to Towers 2026 event.
          Placed before the closing Final CTA so it reads as "next chapter →
          here's how to keep moving with us". Card + data are reused on /events. */}
      {UPCOMING_EVENTS.length > 0 && (
        <section
          data-testid="legacy-events-section"
          className="px-4 md:px-8 py-16 md:py-24 max-w-5xl mx-auto border-t border-white/10"
        >
          <p className="text-[#B32027] uppercase tracking-[0.4em] text-xs font-bold mb-4">/ The movement continues</p>
          <h2 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-4xl md:text-6xl mb-6">
            September ends. <span className="text-[#B32027]">We&rsquo;re not done.</span>
          </h2>
          <p className="text-white/85 text-base md:text-lg leading-relaxed max-w-3xl">
            The 911-Minute Legacy Challenge may end on September 30, but the mission continues. If you&rsquo;re local to Tampa, join <strong>Team Sweat With Slay</strong> for the 2026 Tunnel to Towers Climb as we keep moving in honor of the heroes, families, and legacies we carry with us.
          </p>
          <p className="text-white/75 text-base md:text-lg leading-relaxed mt-3 mb-10 max-w-3xl">
            Whether you completed the September challenge or are just joining the movement, you&rsquo;re invited to climb with us.
          </p>
          <div className="space-y-6">
            {UPCOMING_EVENTS.map((ev) => <EventCard key={ev.id} event={ev} />)}
          </div>
        </section>
      )}

      {/* Final CTA */}
      <SacrificeFinalCTA />

      {/* Donate campaign progress tracker — MOVED to bottom per user request.
          Sits after the emotional Final CTA so the closing note is "here's where
          we stand — help us close the gap." Renders nothing until
          REACT_APP_PLEDGE_TRACKER_ID is set. */}
      <PledgeTracker />

      {/* Share the campaign — personalized "I'm Moving For" graphic
          + universal one-flow sharing across FB / IG / TikTok / LinkedIn / SMS.
          Prefills with the last-submitted signup's moving_for name when available. */}
      <section className="px-4 md:px-8 py-16 bg-[#0A0A0A]">
        <div className="max-w-2xl mx-auto">
          <MovingForShare prefilledName={submitToast?.moving_for || ""} />
        </div>
      </section>

      {/* Sticky bottom CTA bar — always visible on Sacrifice page, hidden while
          the signup form is on-screen. Now a BIG glowing floating box that
          keeps "Join the Legacy" front-and-center throughout the site so
          visitors are consistently guided to complete the signup form. */}
      <div
        data-testid="sticky-cta-bar"
        aria-hidden={signupInView ? "true" : "false"}
        className={`fixed bottom-0 inset-x-0 z-50 transition-all duration-300 ${signupInView ? "opacity-0 translate-y-full pointer-events-none" : "opacity-100 translate-y-0"}`}
      >
        <div className="relative">
          {/* Yellow glow behind the bar so it reads as unmissable */}
          <span aria-hidden className="absolute -inset-2 bg-[#FFC72C] blur-2xl opacity-30 pointer-events-none" />
          <div className="relative bg-[#0A0A0A] border-t-4 border-[#FFC72C] px-4 sm:px-6 py-4 sm:py-5 shadow-[0_-8px_40px_rgba(255,199,44,0.35)]">
            <div className="max-w-7xl mx-auto flex items-center gap-3 sm:gap-5 flex-wrap sm:flex-nowrap">
              <div className="flex-1 min-w-0">
                <p className="text-[#FFC72C] uppercase tracking-[0.3em] text-[10px] sm:text-[11px] font-black leading-none mb-1">
                  / Join the 911-Minute Legacy Challenge
                </p>
                <p className="text-white text-sm sm:text-base font-bold leading-tight">
                  <span className="hidden sm:inline">Sign up in under a minute · then Donate, Shop Merch, or both · </span>
                  <span className="text-[#FFC72C]">Sept 2026</span>
                </p>
              </div>
              <a
                href="#legacy-signup"
                data-testid="sticky-cta-signup"
                onClick={(e) => { e.preventDefault(); document.getElementById("legacy-signup")?.scrollIntoView({ behavior: "smooth", block: "start" }); }}
                className="inline-flex items-center justify-center gap-2 bg-[#FFC72C] hover:bg-yellow-300 text-[#0A0A0A] px-5 sm:px-8 py-3 sm:py-4 text-xs sm:text-sm font-black uppercase tracking-[0.22em] border-2 border-[#0A0A0A] shadow-[0_0_30px_rgba(255,199,44,0.6)] hover:shadow-[0_0_45px_rgba(255,199,44,0.9)] whitespace-nowrap animate-pulse"
              >
                Join the Legacy <ArrowUpRight size={14} weight="bold" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Small-print participant terms — sits at the very bottom so the
          emotional story + signup form stay unobstructed. Covers privacy,
          sharing preferences, safety, merch policies, and the 50/50 split. */}
      <section data-testid="challenge-small-print" className="px-6 md:px-12 py-10 border-t border-white/10 bg-[#0A0A0A]">
        <div className="max-w-3xl mx-auto text-white/45 text-[11px] md:text-xs leading-relaxed space-y-2">
          <p className="text-white/60 uppercase tracking-[0.3em] text-[10px] font-bold mb-2">/ Fine print</p>
          <p>
            Participant information is used to administer the Sweat With Slay Legacy Challenge and to send challenge-related communications. Your name, story, and photos will only be displayed publicly according to the sharing preference you selected on the signup form. You may request that your information, story, or photo be corrected or removed at any time by emailing <a href="mailto:hello@sweatwithslay.com" className="underline hover:text-white">hello@sweatwithslay.com</a>.
          </p>
          <p>
            Participants are responsible for participating safely and within their own abilities. This challenge is not medical advice; consult a healthcare professional before starting any new physical activity. Merchandise fulfillment, shipping, and returns are subject to the applicable store and Printful policies.
          </p>
          <p>
            Every direct donated dollar, along with all net merchandise proceeds (after production, fulfillment, and related merchandise costs), is divided equally between the <a href="https://t2t.org/" target="_blank" rel="noreferrer noopener" className="underline hover:text-white">Tunnel to Towers Foundation</a> and the <a href="https://www.firefamilytransport.org/" target="_blank" rel="noreferrer noopener" className="underline hover:text-white">FDNY Fire Family Transport Foundation</a>. Sweat With Slay never holds donation funds directly — Pledge processes 100&#37; of donations on our behalf.
          </p>
          <p className="pt-2">
            <a href="/privacy" className="underline hover:text-white">Privacy Policy</a>
            <span className="mx-2 opacity-40">·</span>
            <a href="/terms" className="underline hover:text-white">Terms</a>
            <span className="mx-2 opacity-40">·</span>
            <a href="/store-policies" className="underline hover:text-white">Store &amp; Return Policies</a>
          </p>
        </div>
      </section>
    </main>
  );
}
